import {
  Wind,
  Droplets,
  Sun,
  Wrench,
  Sparkles,
  Filter,
  CloudFog,
  Leaf,
  ClipboardList,
  ListChecks,
  Home,
} from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import PageHero from "@/components/site/PageHero";
import ServiceFAQ from "@/components/site/ServiceFAQ";
import RelatedServices from "@/components/site/RelatedServices";
import FinalCTA from "@/components/site/FinalCTA";
import AreaPills from "@/components/site/AreaPills";
import DemoNotice from "@/components/site/DemoNotice";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/51ddf5f6-e80c-4259-9ef6-c5393e7b5903/images/27eabd4237fe10deaf59000323d381461289c590eada597fe702c2fbe8b63aca.png";

const FOCUS = [
  {
    icon: Droplets,
    title: "Humidity control",
    desc: "The single biggest comfort factor in SWFL — dehumidification options that work alongside the AC, not against it.",
  },
  {
    icon: Filter,
    title: "Filtration upgrades",
    desc: "Better filters, sealed return air, and reducing the dust and allergens that recirculate every cycle.",
  },
  {
    icon: Sun,
    title: "UV light options",
    desc: "Coil-mounted UV lights help slow microbial buildup on the indoor coil — a small upgrade with steady benefits.",
  },
  {
    icon: CloudFog,
    title: "Fresh-air ventilation",
    desc: "Bringing in tempered fresh air without throwing humidity control off — a real consideration in tight modern homes.",
  },
  {
    icon: Leaf,
    title: "Allergen & odor reduction",
    desc: "Pollen, pet dander, cooking smells — better IAQ helps the air not just feel cooler, but feel cleaner.",
  },
  {
    icon: Wrench,
    title: "Duct & return-air health",
    desc: "Leaky returns pull in attic air and humidity. Sealing and right-sizing return air is huge in this climate.",
  },
];

const INCLUDED = [
  "Whole-home filtration upgrades",
  "Dehumidifier integration with the existing system",
  "UV light installation at the coil",
  "Duct evaluation and sealing (where needed)",
  "Return-air sizing review",
  "Fresh-air ventilation options",
  "Thermostat / humidistat integration",
  "Recommendations tailored to home age and layout",
];

const TELL_US = [
  {
    icon: ClipboardList,
    title: "What's bothering you today",
    desc: "Humidity, allergies, smells, dust on furniture, asthma — the symptoms drive the recommendation.",
  },
  {
    icon: Home,
    title: "Home age and layout",
    desc: "Older home, newer build, additions, finished attic, whether you've already sealed ducts — all useful.",
  },
  {
    icon: ListChecks,
    title: "What you've already tried",
    desc: "Standalone air purifiers, dehumidifiers, MERV-13 filters — context helps avoid recommending what you've done.",
  },
];

const FAQS = [
  {
    q: "Why is humidity such a problem in Southwest Florida?",
    a: "Florida summers run a lot of moisture through the air, and a standard AC system primarily controls temperature — humidity is a side effect. In long, humid seasons, even a well-sized system can struggle to keep relative humidity in the 45–55% range without help from dedicated humidity control or a variable-speed system.",
  },
  {
    q: "What's the difference between filtration and air purification?",
    a: "Filtration captures particles physically as they move through the system (dust, pollen, dander) — it works on what's in the airflow. Purification (UV, ionization, etc.) addresses different concerns like microbial buildup on coils or volatile compounds. Most homes benefit most from better filtration first, then layered purification where it makes sense.",
  },
  {
    q: "Do I need a UV light in my system?",
    a: "UV lights at the indoor coil help slow microbial buildup, which keeps coils cleaner and helps with musty smells in humid climates. They're not a magic fix for allergies on their own, but they're a steady, low-maintenance upgrade many SWFL homes appreciate.",
  },
  {
    q: "Will fresh-air ventilation help?",
    a: "In newer, tightly-built homes, fresh-air ventilation prevents stale indoor air and helps with CO2 and VOC buildup. In a humid climate it has to be done carefully — bringing in raw humid air can undo dehumidification work. A balanced or tempered ventilation strategy usually works best.",
  },
  {
    q: "Will an IAQ upgrade lower my power bill?",
    a: "Not always directly. Some upgrades — like sealing leaky ductwork or right-sizing returns — can absolutely reduce runtime and bills. Others, like UV or filtration, are about air quality, not efficiency. A real contractor will tell you which is which.",
  },
  {
    q: "Is this a real HVAC company?",
    a: 'Sun Chaser Cooling is a sample HVAC website created by PrismForge Digital. <a class="text-aqua-deep underline-offset-2 hover:underline" href="/demo">See how this demo works →</a>',
    html: true,
  },
];

export default function IndoorAirQuality() {
  useSeo({
    title:
      "Indoor Air Quality in Southwest Florida | Sun Chaser Cooling — PrismForge Digital Demo",
    description:
      "Demo IAQ page for humid SWFL homes. Humidity control, filtration upgrades, UV options, and fresh-air ventilation — built by PrismForge Digital.",
  });

  return (
    <div data-testid="iaq-page">
      <PageHero
        eyebrow="Indoor Air Quality"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Services", to: "/services" },
          { label: "Indoor Air Quality" },
        ]}
        title="Indoor air quality"
        highlight="in a humid coastal climate."
        subtitle="Dehumidification, filtration, UV, and ventilation options for homes that fight humidity year round."
        primaryCta={{
          label: "Request IAQ Assessment",
          to: "/request-service?intent=indoor-air-quality",
        }}
        secondaryCta={{
          label: "Schedule Maintenance",
          to: "/request-service?intent=maintenance",
        }}
        image={HERO_IMG}
        imageAlt="A residential ceiling supply vent with soft airflow lines — illustrating indoor air movement and quality."
        variant="light"
        testId="iaq-hero"
      />

      {/* What to look at */}
      <section
        data-testid="iaq-focus-section"
        aria-labelledby="iaq-focus-heading"
        className="relative bg-white py-20 sm:py-24"
      >
        <div className="container-page">
          <div className="max-w-2xl">
            <span className="pill">
              <Wind className="h-3 w-3 text-aqua" />
              What we look at
            </span>
            <h2
              id="iaq-focus-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              Six places IAQ improvements pay off.
            </h2>
            <p className="mt-4 text-base text-navy/70 sm:text-lg">
              In a humid climate, the air can be the right temperature and
              still feel uncomfortable. Most IAQ wins come from these.
            </p>
          </div>
          <ul className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FOCUS.map((f) => {
              const Icon = f.icon;
              const slug = f.title
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/(^-|-$)/g, "");
              return (
                <li
                  key={f.title}
                  data-testid={`iaq-focus-${slug}`}
                  className="rounded-2xl border border-mist-line bg-white p-6 shadow-soft transition-transform hover:-translate-y-1"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-navy text-aqua">
                    <Icon className="h-4 w-4" />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-bold leading-tight tracking-tight text-navy">
                    {f.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-navy/70">
                    {f.desc}
                  </p>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* What's included */}
      <section
        data-testid="iaq-included-section"
        aria-labelledby="iaq-included-heading"
        className="relative bg-mist py-20 sm:py-24"
      >
        <div className="container-page grid grid-cols-1 items-start gap-12 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-5">
            <span className="pill">
              <Sparkles className="h-3 w-3 text-aqua" />
              What an IAQ visit covers
            </span>
            <h2
              id="iaq-included-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              A practical scope, not a sales pitch.
            </h2>
            <p className="mt-4 max-w-lg text-base text-navy/70 sm:text-lg">
              The honest version: most homes don&rsquo;t need every upgrade.
              The visit is about figuring out which ones matter for your
              house.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="rounded-2xl border border-mist-line bg-white shadow-soft">
              {INCLUDED.map((line, i) => (
                <li
                  key={line}
                  className={`flex items-center justify-between gap-3 px-5 py-4 ${
                    i < INCLUDED.length - 1 ? "border-b border-mist-line" : ""
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-aqua/15 font-display text-xs font-bold text-aqua-deep">
                      {i + 1}
                    </span>
                    <p className="text-[15px] font-medium text-navy/85">
                      {line}
                    </p>
                  </div>
                  <span className="hidden text-[10px] font-semibold uppercase tracking-[0.16em] text-navy/40 sm:inline">
                    Reviewed
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* What to tell us */}
      <section
        data-testid="iaq-tell-section"
        aria-labelledby="iaq-tell-heading"
        className="relative bg-white py-20 sm:py-24"
      >
        <div className="container-page">
          <div className="max-w-2xl">
            <span className="pill">
              <ClipboardList className="h-3 w-3 text-aqua" />
              What to tell us
            </span>
            <h2
              id="iaq-tell-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              Help us understand your home.
            </h2>
          </div>
          <ul className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
            {TELL_US.map((t) => {
              const Icon = t.icon;
              return (
                <li
                  key={t.title}
                  className="flex flex-col rounded-2xl border border-mist-line bg-white p-6 shadow-soft"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-navy text-aqua">
                    <Icon className="h-4 w-4" />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-bold leading-tight tracking-tight text-navy">
                    {t.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-navy/70">
                    {t.desc}
                  </p>
                </li>
              );
            })}
          </ul>
          <div className="mt-8">
            <DemoNotice
              variant="dark"
              label="Demo intake captures IAQ symptoms so the visit is targeted, not generic."
            />
          </div>
        </div>
      </section>

      {/* SWFL context */}
      <section
        data-testid="iaq-swfl-section"
        aria-labelledby="iaq-swfl-heading"
        className="relative bg-mist py-20 sm:py-24"
      >
        <div className="container-page max-w-4xl">
          <span className="pill">
            <span className="h-1.5 w-1.5 rounded-full bg-aqua" />
            Southwest Florida context
          </span>
          <h2
            id="iaq-swfl-heading"
            className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
          >
            Coastal humidity is a year-round IAQ problem.
          </h2>
          <div className="mt-5 space-y-4 text-base leading-relaxed text-navy/75 sm:text-lg">
            <p>
              In SWFL, indoor air quality is mostly a humidity story. High
              relative humidity makes rooms feel warmer than they are,
              encourages microbial growth on coils, and amplifies allergen
              symptoms. A standard AC system handles temperature first; real
              comfort comes from also handling moisture.
            </p>
            <p>
              The best IAQ improvements here usually start with sealing leaky
              returns, upgrading filtration, and giving the system a way to
              dehumidify even when the temperature is already where you want
              it.
            </p>
          </div>
          <div className="mt-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/55">
              IAQ improvements across SWFL
            </p>
            <AreaPills className="mt-3" />
          </div>
        </div>
      </section>

      <ServiceFAQ
        title="Common IAQ questions."
        subtitle="Honest, practical answers — no scare tactics."
        faqs={FAQS}
        testId="iaq-faq"
      />

      <RelatedServices currentSlug="indoor-air-quality" />

      <FinalCTA
        title="Get an IAQ assessment that's actually useful."
        subtitle="Tell us what's bothering you — we'll match the upgrade to the symptom."
        primaryCta={{
          label: "Request IAQ Assessment",
          to: "/request-service?intent=indoor-air-quality",
          icon: Wind,
        }}
      />
    </div>
  );
}
